import { computed, nextTick, ref } from 'vue';
import CustomPhoneField from '@/components/inputs/form/CustomPhoneField.vue';
import CustomEmailField from '@/components/inputs/form/CustomEmailField.vue';
import { Contact } from '@/models/class';
import { useRoadbookStore, useTagManagerStore } from '@/store';
import { useRouter } from 'vue-router';
import { Recipient } from '@/models/interfaces/Recipient.interface';
import apiRoadbookService from '@/services/api/roadbookService';

export function useContactModal(props: {
  close: () => void;
  emit: (e: 'loading', isLoading: boolean) => void;
}) {
  const roadbookStore = useRoadbookStore();
  const tagManagerStore = useTagManagerStore();
  const router = useRouter();

  const contact = ref<Contact>(roadbookStore.metadata.contact[0] ?? new Contact());
  const customTelRef = ref<typeof CustomPhoneField>();
  const isCustomTelRefRequired = ref(false);
  const customEmailRef = ref<typeof CustomEmailField>();
  const isCustomEmailRefRequired = ref(false);

  const isContactEmpty = computed(() => contact.value.email === '' && contact.value.phone === '');

  /* Used to notify the user that at least one contact method (email address or phone number) is mandatory. */
  const updateRequired = async () => {
    await nextTick();

    if (!customTelRef.value || !customEmailRef.value) return;

    if (isContactEmpty.value) {
      isCustomTelRefRequired.value = false;
      isCustomEmailRefRequired.value = false;
    } else {
      const isEmailRequired = !customTelRef.value.checkValidity();
      const isTelRequired = !customEmailRef.value.checkValidity();

      isCustomTelRefRequired.value = isTelRequired;
      isCustomEmailRefRequired.value = isEmailRequired;
    }
  };

  const handleClose = () => {
    contact.value = new Contact();
  };

  const save = async (e: SubmitEvent) => {
    /*
     * Looks for all phone inputs not to be .invalid before submitting the form.
     */
    if (e.target instanceof HTMLElement) {
      const invalidPhone = Array.from(
        e.target.querySelectorAll('input[type="tel"].invalid'),
      ).length;

      if (invalidPhone) return;
    }

    props.emit('loading', true);
    roadbookStore.upsertContact(contact.value);

    /** ENVOI A NURTIK : */
    roadbookStore.metadata.recipients = [];

    const recipients: Array<Recipient> = [];
    if (contact.value.email) {
      recipients.push({
        ...contact.value,
        type: 'email',
        value: contact.value.email,
        date: new Date().toISOString(),
        success: false,
      });
    }
    if (contact.value.phone) {
      recipients.push({
        ...contact.value,
        type: 'phone',
        value: contact.value.phone,
        date: new Date().toISOString(),
        success: false,
      });
    }

    if (recipients.length) {
      const result = await apiRoadbookService.webhookQualif(
        roadbookStore.url + roadbookStore.id,
        roadbookStore.metadata.visitor.language || 'fr',
        recipients,
        roadbookStore.currentEntity,
      );

      if (result)
        recipients.map((recipient) => {
          return { ...recipient, successes: true };
        });

      recipients.forEach((recipient) => {
        roadbookStore.updateMetadata({ category: 'recipients', field: '', value: recipient });
      });
    }
    /** Fin envoi Nurtik */

    roadbookStore.updateMetadata({
      category: 'finished',
      field: null,
      value: true,
    });
    tagManagerStore.sendEvent({
      event: 'roadbook_finish',
    });
    props.close();
    await roadbookStore.restart();
    router.push('dashboard');
    props.emit('loading', false);
  };

  return {
    contact,
    isContactEmpty,
    customTelRef,
    isCustomTelRefRequired,
    customEmailRef,
    isCustomEmailRefRequired,
    updateRequired,
    handleClose,
    save,
  };
}
